import "./report-stamp.css";
import { EnemyMark } from "./strike-page";
/**
 * Reporting in on the laptop: a report window on its desktop, in the case's
 * colours like the mission page. The mission sits under its name (in case
 * they forget it); then what they did, how it felt, one photo or video as
 * evidence, and whether to offer it for review — filed exactly as the paper
 * debrief files it (useFileDebrief, then feature-review).
 */
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type TextareaHTMLAttributes,
} from "react";
import { toast } from "sonner";
import { isAxiosError } from "axios";
import { ImagePlus, X } from "lucide-react";
import { useFileDebrief } from "@/features/funnel/api/use-mission-file";
import type { Mission } from "@/features/missionkit/api/use-mission";
import type { Tier } from "@/features/funnel/types";
import { paletteVars } from "./open-case";
import { useStepperStore } from "@/store";
import { useReportDraft } from "./report-draft";
import { VoiceReport, type ReportDraft } from "./voice-report";
import { missionStepsEnabled } from "./comeback-config";
import { stepTextsFor } from "./mission-steps";
import { useFunnelStore } from "@/store/funnel";
import { DebriefTaps } from "./debrief-taps";
import {
  EMPTY_ANSWERS,
  answersReady,
  composeReport,
  type DebriefAnswers,
} from "./debrief-answers";
import { LOCK_WORDS } from "./vault-clips";

// Django's DATA_UPLOAD_MAX_MEMORY_SIZE, as on the paper debrief
const MAX_MEDIA_BYTES = 200 * 1024 * 1024;

const FIELD_CLASS =
  "w-full resize-none rounded-[14px] border-0 bg-[var(--paper)] px-4 py-3 text-[16px] text-[var(--ink)] shadow-[0_1px_0_rgba(255,255,255,.6)_inset,0_6px_16px_color-mix(in_srgb,var(--ink)_10%,transparent)] outline-none ring-[color-mix(in_srgb,var(--ink)_70%,transparent)] transition-shadow placeholder:text-[var(--muted)] focus:ring-2";

/** Keep full report drafts visible, including prefilled voice summaries. */
function ReportTextArea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const field = useRef<HTMLTextAreaElement>(null);
  const resize = useRef(() => {
    const node = field.current;
    if (!node) return;
    node.style.height = "auto";
    node.style.height = `${node.scrollHeight}px`;
  });
  // Re-measure height whenever the value changes.
  useLayoutEffect(() => {
    resize.current();
  }, [props.value]);
  // Observe container-width changes once; no teardown/rebuild on each keystroke.
  useLayoutEffect(() => {
    const node = field.current;
    if (!node) return;
    if (typeof ResizeObserver === "undefined") {
      const handler = () => resize.current();
      window.addEventListener("resize", handler);
      return () => window.removeEventListener("resize", handler);
    }
    let width = node.clientWidth;
    const observer = new ResizeObserver(() => {
      if (node.clientWidth === width) return;
      width = node.clientWidth;
      resize.current();
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return (
    <textarea
      {...props}
      ref={field}
      style={{ ...props.style, overflowY: "hidden" }}
    />
  );
}

/** A numbered step's heading: the number in a badge, then its name. */
function Step({ n, children }: { n: number; children: string }) {
  return (
    <span className="mb-2 flex items-center gap-2.5 text-[15px] font-bold text-[var(--ink)]">
      <span className="grid size-6 place-items-center rounded-full bg-[var(--ink)] text-[12.5px] text-[var(--glow)]">
        {n}
      </span>
      {children}
    </span>
  );
}

export function ReportWindow({
  tier,
  mission,
  onSubmitTap,
  onFiled,
  onClose,
  mode = "written",
  onBranch,
  lockNo,
}: {
  mode?: "voice" | "written";
  onBranch?: (event: "GOALMET" | "CANTTALK") => void;
  tier: Tier;
  mission: Mission;
  /** the lock this report opens (1 = their first), for the payoff line */
  lockNo?: number;
  /** the submit tap itself (the vault clip is started inside it) */
  onSubmitTap: () => void;
  onFiled: () => void;
  onClose: () => void;
}) {
  const fileDebrief = useFileDebrief();
  const alive = useRef(true);
  const pending = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const adventureId = useStepperStore((state) => state.adventureId);
  const draftKey = `${adventureId}:${mission.missionId}`;
  const savedDraft = useReportDraft.getState().drafts[draftKey];
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const [whatIDid, setWhatIDid] = useState(savedDraft?.whatIDid ?? "");
  const [howItFelt, setHowItFelt] = useState(savedDraft?.howItFelt ?? "");
  // the Comeback Loop: the written debrief as taps (the voice mode and what
  // gets filed are unchanged)
  // (every mission, with the steps build: a mission without steps asks for
  // their own line in place of the account)
  // A voice review (or a partial written draft) already contains their own
  // account. Keep it editable rather than replacing it with fresh tap answers.
  const [hasDraft] = useState(
    () =>
      !!(
        savedDraft?.whatIDid.trim() ||
        savedDraft?.howItFelt.trim() ||
        savedDraft?.reflectionOptional
      ),
  );
  const taps = missionStepsEnabled() && mode === "written" && !hasDraft;
  const [answers, setAnswers] = useState<DebriefAnswers>(EMPTY_ANSWERS);
  const handler = useFunnelStore((s) => s.handler);
  const graph = useStepperStore((state) => state.graph);
  const steps = stepTextsFor(graph, mission);
  const tapped = composeReport(answers, steps);
  const [media, setMedia] = useState<File | null>(null);
  const [forReview, setForReview] = useState(false);
  const takeBranch = (event: "GOALMET" | "CANTTALK", draft: ReportDraft) => {
    useReportDraft.getState().save(draftKey, draft);
    onBranch?.(event);
  };
  const picker = useRef<HTMLInputElement>(null);
  const preview = useMemo(
    () => (media ? URL.createObjectURL(media) : null),
    [media],
  );
  useEffect(
    () => () => {
      if (preview) URL.revokeObjectURL(preview);
    },
    [preview],
  );
  const isVideo = !!media?.type.startsWith("video/");
  const reflectionOptional = savedDraft?.reflectionOptional === true;
  const ready = taps
    ? answersReady(answers)
    : whatIDid.trim() !== "" && (howItFelt.trim() !== "" || reflectionOptional);
  // on one page: proof, share and the file button are always there (the
  // button waits for the three taps)
  // page two of the taps (the outcome chosen) carries proof, share and
  // the file button; page one is the question alone
  const tapsDone = !taps || !!answers.outcome;
  const task = mission.mission
    .replace(/^your mission:\s*/i, "")
    .trim()
    .replace(/^./, (c) => c.toUpperCase());

  const pick = (file: File | undefined) => {
    if (!file) return;
    if (file.size > MAX_MEDIA_BYTES) {
      toast.error("That file is too big — 200 MB max.");
      return;
    }
    setMedia(file);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (mode === "voice" || !ready || pending.current) return;
    pending.current = true;
    setSubmitting(true);
    (document.activeElement as HTMLElement | null)?.blur();
    onSubmitTap();
    try {
      await fileDebrief.mutateAsync({
        missionName: mission.name || "Secret mission",
        missionId: mission.missionId,
        shareAsExample: forReview,
        whatIDid: taps ? tapped.whatIDid : whatIDid.trim(),
        howItFelt: taps ? tapped.howItFelt : howItFelt.trim(),
        media,
      });
      useReportDraft.getState().clear(draftKey);
      if (alive.current) onFiled();
    } catch (err) {
      if (!alive.current) return;
      const uploadFailed =
        media &&
        isAxiosError(err) &&
        err.response?.data?.message === "Error uploading media file";
      toast.error(
        uploadFailed
          ? "Your evidence couldn't be uploaded. Remove it to file without it, or try again."
          : "Couldn't file your report — try again.",
      );
    } finally {
      pending.current = false;
      if (alive.current) setSubmitting(false);
    }
  };

  return (
    <div
      style={paletteVars(tier)}
      className={`absolute inset-0 flex flex-col bg-[var(--day)] pt-[44px] ${mode === "voice" ? "report-window-voice" : ""}`}
    >
      {/* just the way out, top right (no title bar) */}
      <div
        className={
          mode === "voice" ? "control-close" : "flex justify-end px-3 pt-2"
        }
      >
        <button
          type="button"
          disabled={submitting}
          onClick={() => {
            if (pending.current) return;
            (document.activeElement as HTMLElement | null)?.blur();
            if (mode === "written")
              useReportDraft.getState().save(draftKey, {
                whatIDid,
                howItFelt,
                ...(reflectionOptional ? { reflectionOptional: true } : {}),
              });
            onClose();
          }}
          aria-label="Close"
          className="grid size-9 cursor-pointer place-items-center rounded-full bg-[color-mix(in_srgb,var(--ink)_10%,transparent)] text-[var(--ink)]"
        >
          <X className="size-5" />
        </button>
      </div>

      <form
        id="report-form"
        onSubmit={submit}
        className={
          mode === "voice"
            ? "control-recorder-frame"
            : taps
              ? "flex min-h-0 flex-1 flex-col justify-center gap-2.5 overflow-hidden px-5 pb-2 [&>*]:shrink-0"
              : "flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto overscroll-contain px-5 pt-1 pb-5 [&>*]:shrink-0"
        }
      >
        {taps ? (
          <DebriefTaps
            handler={handler}
            missionName={mission.name || "this mission"}
            answers={answers}
            onChange={setAnswers}
          />
        ) : null}
        {mode !== "voice" && !taps && (
          <>
            <div>
              <h2 className="text-[24px] leading-[1.1] font-extrabold tracking-[-.02em] text-[var(--ink)]">
                Review your report.
              </h2>
              <p className="mt-1 text-[14px] text-[color-mix(in_srgb,var(--ink)_70%,transparent)]">
                Edit your debrief, add an optional photo or video, then file
                your report.
              </p>
            </div>
            {mission.strike?.trim() && (
              <section
                aria-label="The enemy you beat"
                className="min-w-0 rounded-[14px] border border-[var(--glow)] bg-[#171315] px-4 py-4 text-[#f4ece4]"
              >
                <div
                  role="img"
                  className="report-enemy-seal relative mx-auto mb-3 grid size-[94px] place-items-center"
                  aria-label="Enemy defeated"
                >
                  <EnemyMark tone="screen" className="size-[80px] opacity-70" />
                  <span className="report-defeated-stamp absolute inset-x-0 top-1/2 -translate-y-1/2 -rotate-12 rounded border-2 border-[#55e092] bg-[#10291d]/95 py-1 text-center font-mono text-[15px] leading-none font-black tracking-[.02em] text-[#76f3aa] shadow-[0_3px_16px_#0009]">
                    DEFEATED
                  </span>
                </div>
                <h3 className="text-left text-[15px] font-bold text-[var(--glow)]">
                  The enemy you beat
                </h3>
                <p className="mt-2 whitespace-pre-line text-[16px] leading-relaxed [overflow-wrap:anywhere]">
                  {mission.strike.trim()}
                </p>
              </section>
            )}
            {/* the mission itself, in case they have forgotten it: on a card
            like the Enemy's Strike on the mission page */}
            <div className="relative overflow-hidden rounded-[14px] bg-[linear-gradient(160deg,#241a16,#120d0b)] px-4 pt-3 pb-3.5 text-[#f4ece4] shadow-[inset_4px_0_0_var(--glow),0_10px_24px_rgba(0,0,0,.25)]">
              <div className="flex items-center gap-2 text-[12px] font-bold text-[var(--glow)]">
                <span className="size-[7px] rounded-full bg-[var(--glow)] shadow-[0_0_8px_var(--glow)]" />
                Your mission · {mission.name}
              </div>
              <p className="mt-1.5 text-[14.5px] leading-[1.4]">{task}</p>
              <div className="pointer-events-none absolute inset-0 bg-[repeating-linear-gradient(0deg,rgba(255,255,255,.03)_0_1px,transparent_1px_3px)]" />
            </div>
          </>
        )}
        {mode === "voice" ? (
          <VoiceReport
            mission={mission}
            onReview={(draft) => takeBranch("GOALMET", draft)}
            onFallback={(draft) => takeBranch("CANTTALK", draft)}
            onPause={onClose}
          />
        ) : (
          <>
            {!taps ? (
              <>
                <label className="block">
                  <Step n={1}>What I did</Step>
                  <ReportTextArea
                    rows={3}
                    value={whatIDid}
                    onChange={(e) => setWhatIDid(e.target.value)}
                    placeholder="Who, where, what you said or did."
                    className={FIELD_CLASS}
                  />
                </label>
                <label className="block">
                  <Step n={2}>
                    {reflectionOptional
                      ? "How it felt (optional)"
                      : "How it felt"}
                  </Step>
                  <ReportTextArea
                    rows={2}
                    value={howItFelt}
                    onChange={(e) => setHowItFelt(e.target.value)}
                    placeholder={
                      reflectionOptional
                        ? "You can leave this blank."
                        : "Before, and after."
                    }
                    className={FIELD_CLASS}
                  />
                </label>
              </>
            ) : null}

            {/* evidence: one photo or video */}
            {tapsDone ? (
              <div
                style={taps ? { animationDelay: "1300ms" } : undefined}
                className={taps ? "animate-[smTapsIn_.45s_ease-out_both]" : ""}
              >
                {!taps ? <Step n={3}>Evidence</Step> : null}
                <input
                  ref={picker}
                  type="file"
                  accept="image/*,video/*"
                  className="hidden"
                  onChange={(e) => {
                    pick(e.target.files?.[0]);
                    e.target.value = "";
                  }}
                />
                {preview ? (
                  <div className="relative">
                    {isVideo ? (
                      <video
                        src={preview}
                        muted
                        loop
                        autoPlay
                        playsInline
                        className="max-h-[30dvh] w-full rounded-[10px] bg-black object-cover"
                      />
                    ) : (
                      <img
                        src={preview}
                        alt=""
                        className="max-h-[30dvh] w-full rounded-[10px] object-cover"
                      />
                    )}
                    <button
                      type="button"
                      onClick={() => setMedia(null)}
                      aria-label="Remove"
                      className="absolute top-2 right-2 grid size-8 cursor-pointer place-items-center rounded-full bg-black/70 text-white"
                    >
                      <X className="size-4" />
                    </button>
                  </div>
                ) : taps ? (
                  <button
                    type="button"
                    onClick={() => picker.current?.click()}
                    className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-[12px] border border-[color-mix(in_srgb,var(--ink)_40%,transparent)] bg-transparent px-4 py-2.5 text-[15px] font-bold text-[var(--ink)] transition-[transform,background-color] duration-150 hover:bg-[color-mix(in_srgb,var(--ink)_8%,transparent)] active:scale-[.98]"
                  >
                    <ImagePlus className="size-5 shrink-0" />
                    Add a photo
                    <span className="text-[12.5px] font-medium text-[var(--muted)]">
                      (optional)
                    </span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => picker.current?.click()}
                    className="flex w-full cursor-pointer items-center gap-3.5 rounded-[14px] border-2 border-dashed border-[color-mix(in_srgb,var(--ink)_28%,transparent)] bg-[color-mix(in_srgb,var(--paper)_45%,transparent)] px-4 py-3.5 text-left text-[var(--ink)]"
                  >
                    <span className="grid size-11 shrink-0 place-items-center rounded-full bg-[var(--ink)] text-[var(--glow)]">
                      <ImagePlus className="size-5" />
                    </span>
                    <span>
                      <span className="block text-[15px] font-bold">
                        {taps ? "Got a photo?" : "Add a photo or video"}
                      </span>
                      <span className="block text-[13px] text-[var(--muted)]">
                        {taps
                          ? "Agents love proof. Optional."
                          : "Optional, but agents love proof."}
                      </span>
                    </span>
                  </button>
                )}

                {/* offer it for review: a card with a checkbox */}
                <label
                  className={`flex cursor-pointer items-center gap-3 text-[var(--ink)] ${
                    taps
                      ? "mt-1 justify-center px-2 py-1"
                      : "items-start rounded-[14px] bg-[var(--paper)] px-4 py-3 shadow-[0_6px_16px_color-mix(in_srgb,var(--ink)_10%,transparent)]"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={forReview}
                    onChange={(e) => setForReview(e.target.checked)}
                    className="size-5 shrink-0 cursor-pointer accent-[var(--ink)]"
                  />
                  <span>
                    <span
                      className={`block text-[15px] ${taps ? "font-medium" : "font-bold"}`}
                    >
                      Share as a tactic
                    </span>
                    {!taps ? (
                      <span className="block text-[13px] leading-[1.35] text-[var(--muted)]">
                        Submit for review. Your report could help other agents
                        counter this strike.
                      </span>
                    ) : null}
                  </span>
                </label>
                {/* the payoff, before the tap */}
                {taps && lockNo ? (
                  <p className="mt-1 text-center font-mono text-[10.5px] tracking-[.18em] text-[var(--ink)] uppercase">
                    Filing opens lock {Math.min(lockNo, LOCK_WORDS.length)} of{" "}
                    {LOCK_WORDS.length}
                  </p>
                ) : null}
              </div>
            ) : null}
          </>
        )}
      </form>

      {/* its own bar at the bottom, on a solid background: the report
          scrolls above it, never under it */}
      {mode === "written" && tapsDone && (
        <div className="shrink-0 border-t border-[color-mix(in_srgb,var(--ink)_14%,transparent)] bg-[var(--day)] px-5 pt-3 pb-[max(16px,env(safe-area-inset-bottom))]">
          <button
            type="submit"
            form="report-form"
            disabled={!ready || submitting}
            className={
              taps
                ? "w-full cursor-pointer rounded-[16px] border border-[#d6a94e]/35 bg-[linear-gradient(180deg,#2e261c_0%,#1a1410_55%,#120e0a_100%)] py-4 text-[18px] font-bold text-[#d6a94e] shadow-[inset_0_1px_0_rgba(255,236,180,.14),inset_0_-3px_0_rgba(0,0,0,.5),0_3px_0_#070503,0_10px_24px_rgba(0,0,0,.3)] transition-[translate,box-shadow,filter,opacity] hover:brightness-110 active:translate-y-[3px] active:shadow-[inset_0_1px_0_rgba(255,236,180,.14),0_0_0_#070503,0_4px_10px_rgba(0,0,0,.3)] disabled:opacity-60 disabled:hover:brightness-100"
                : "w-full cursor-pointer rounded-[16px] bg-[#1a1410] py-4 text-[18px] font-bold text-[var(--glow)] shadow-[0_10px_24px_rgba(0,0,0,.25)] transition-transform active:scale-[.98] disabled:opacity-45"
            }
          >
            {submitting
              ? "Filing…"
              : taps
                ? "File it. Open the lock."
                : "File my report"}
          </button>
        </div>
      )}
    </div>
  );
}
